import React from 'react';
import ReactDOM from 'react-dom';

const host = "prod-saver-fknyk2pnlq-an.a.run.app";
const shop = "waza2-store.myshopify.com";

// クエリパラメータを取得する関数
const getQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  const entries = {};
  for (const [key, value] of params.entries()) {
    if (entries[key]) {
      entries[key].push(value);
    } else {
      entries[key] = [value];
    }
  }
  return entries;
}

// 価格範囲を更新する関数
const updatePriceRange = (data) => {
  const priceRangeElements = document.querySelectorAll('price-range');
  priceRangeElements.forEach(priceRangeElement => {
    const rangeMax = Math.ceil(data.range_max);
    const minValue = Math.ceil((priceRangeElement.dataset.filterMin / 100.0) || 0);
    const maxValue = Math.ceil((priceRangeElement.dataset.filterMax ? priceRangeElement.dataset.filterMax / 100.0 : rangeMax));
    const stepValue = Math.ceil(Math.max(rangeMax / 100.0, 1));
    const lowerBoundProgress = (minValue / rangeMax) * 100.0;
    const higherBoundProgress = (maxValue / rangeMax) * 100.0;

    const rangeLowerBound = priceRangeElement.querySelector('input[type="range"]:first-child');
    const rangeHigherBound = priceRangeElement.querySelector('input[type="range"]:last-child');
    rangeLowerBound.max = rangeMax;
    rangeHigherBound.max = rangeMax;
    rangeLowerBound.step = stepValue;
    rangeHigherBound.step = stepValue;
    rangeLowerBound.value = minValue;
    rangeHigherBound.value = maxValue;

    rangeLowerBound.parentElement.style.setProperty("--range-min", `${lowerBoundProgress}%`);
    rangeHigherBound.parentElement.style.setProperty("--range-max", `${higherBoundProgress}%`);

    const textInputLowerBound = priceRangeElement.querySelector('input[name="filter.v.price.gte"]');
    const textInputHigherBound = priceRangeElement.querySelector('input[name="filter.v.price.lte"]');
    textInputLowerBound.value = minValue;
    textInputHigherBound.value = maxValue;
  });
}

// 検索結果件数を更新する関数
const updateSearchResultCount = (data) => {
  const resultCountElement = document.getElementById('js-main-search__search-result-number');
  if (resultCountElement) {
    const articleResultsSize = parseInt(resultCountElement.getAttribute('data-article-results-size'), 10) || 0;
    const totalResults = data.count + articleResultsSize;
    resultCountElement.textContent = `${totalResults}件`;
  }
}

// ページネーションリンクコンポーネント
const PaginationLink = ({ href, children, rel, className, ariaCurrent }) => (
  <a href={href} rel={rel} className={className} aria-current={ariaCurrent}>
    {children}
  </a>
);

// ページネーションコンポーネント
const Pagination = ({ totalPages, currentPage, queryParams }) => {
  if (totalPages <= 1) return null;

  const createPageLink = (page) => {
    // クエリパラメータのコピーを作成してページ番号を更新
    const newParams = new URLSearchParams(queryParams);
    newParams.set('page', page);
    return `/search?${newParams.toString()}`;
  }

  const pageLinks = [];
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);

  if (startPage > 1) {
    pageLinks.push(
      <PaginationLink
        key={1}
        href={createPageLink(1)}
        className="pagination__link h6"
      >
        1
      </PaginationLink>
    );
    if (startPage > 2) {
      pageLinks.push(
        <span key="start-ellipsis" className="pagination__ellipsis">…</span>
      );
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pageLinks.push(
      <PaginationLink
        key={i}
        href={createPageLink(i)}
        className={`pagination__link h6 ${i === currentPage ? 'pagination__link--disabled' : ''}`}
        ariaCurrent={i === currentPage ? 'page' : null}
      >
        {i}
      </PaginationLink>
    );
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      pageLinks.push(
        <span key="end-ellipsis" className="pagination__ellipsis">…</span>
      );
    }
    pageLinks.push(
      <PaginationLink
        key={totalPages}
        href={createPageLink(totalPages)}
        className="pagination__link h6"
      >
        {totalPages}
      </PaginationLink>
    );
  }

  return (
    <div className="sec-main-search__pagenation-wrap">
      <nav className="sec-main-search__pagenation pagination" role="navigation">
        {currentPage > 1 && (
          <PaginationLink href={createPageLink(currentPage - 1)} rel="prev" className="pagination__link h6">
            <svg aria-hidden="true" focusable="false" fill="none" width="11" className="icon icon-chevron-left icon--direction-aware" viewBox="0 0 10 10">
              <path d="M7 1 3 5l4 4" stroke="currentColor" strokeLinecap="square"></path>
            </svg>
          </PaginationLink>
        )}
        {pageLinks}
        {currentPage < totalPages && (
          <PaginationLink href={createPageLink(currentPage + 1)} rel="next" className="pagination__link h6">
            <svg aria-hidden="true" focusable="false" fill="none" width="11" className="icon icon-chevron-right icon--direction-aware" viewBox="0 0 10 10">
              <path d="m3 9 4-4-4-4" stroke="currentColor" strokeLinecap="square"></path>
            </svg>
          </PaginationLink>
        )}
      </nav>
    </div>
  );
}

// エラーコンポーネント
const ErrorMessage = ({ message }) => (
  <div className="sec-main-search__empty-state empty-state">
    <div className="prose">
      <p>{message}</p>
    </div>
  </div>
);

// Productコンポーネント
const Product = ({ product }) => {
  const isOnSale = product.min_variant_compare_at_price > 0 || product.max_variant_compare_at_price > 0;
  return (
    <div className="pr_product-card product-card">
      <div className="product-card__figure">
        {product.in_stock === false && (
          <div className="badge-list badge-list--vertical">
            <div className="badge badge--sold-out">売り切れ</div>
          </div>
        )}
        <a href={`/products/${product.handle}`} alt={product.title} className="product-card__media aspect-tall2">
          <img
            src={product.featured_image}
            alt={product.title}
            loading="lazy"
            className="product-card__image product-card__image--primary object-cover aspect-tall2"
          />
        </a>
      </div>
      <div className="product-card__info empty:hidden">
        <div className="product-card__info-head">
          <a href={`/products/${product.handle}`} className="product-title h6">{product.title}</a>
        </div>
        <div className="product-card__info-foot">
          {isOnSale && product.min_variant_price < product.min_variant_compare_at_price ? (
            <div className="price-list price-list--product">
              <div className="h4 text-on-sale">
                <span className="sr-only">セール価格</span>
                {product.min_variant_price === product.max_variant_price
                  ? `¥${product.min_variant_price}`
                  : `¥${product.min_variant_price}〜`}
              </div>
              <div className="h4 text-subdued line-through">
                <span className="sr-only">通常価格</span>
                {product.min_variant_compare_at_price === product.max_variant_compare_at_price
                  ? `¥${product.min_variant_compare_at_price}`
                  : `¥${product.min_variant_compare_at_price}〜`}
              </div>
            </div>
          ) : (
            <div className="price-list price-list--product">
              <div className="h4">
                <span className="sr-only">価格</span>
                {product.min_variant_price === product.max_variant_price
                  ? `¥${product.min_variant_price}`
                  : `¥${product.min_variant_price}〜`}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ProductListコンポーネント
const ProductList = ({ products }) => {
  return (
    <div className="sec-main-search__product-list product-list">
      {products.map((product, index) => (
        <Product key={index} product={product} />
      ))}
    </div>
  );
};

// データを取得してレンダリングする関数
const fetchDataAndRender = () => {
  const queryParams = getQueryParams();

  // fetch用のクエリパラメータを作成
  let searchStr = `shop=${shop}&limit=48`;
  if (queryParams.q && queryParams.q.length > 0) {
    const encodedKeyword = encodeURIComponent(queryParams.q[0]);
    searchStr += "&keyword=" + encodedKeyword;
  }
  if (queryParams["filter.v.availability"] && queryParams["filter.v.availability"].length > 0) {
    searchStr += "&instock=" + queryParams["filter.v.availability"][0];
  }
  if (queryParams["filter.v.price.gte"] && queryParams["filter.v.price.gte"].length > 0) {
    searchStr += "&min_price=" + queryParams["filter.v.price.gte"][0];
  }
  if (queryParams["filter.v.price.lte"] && queryParams["filter.v.price.lte"].length > 0) {
    searchStr += "&max_price=" + queryParams["filter.v.price.lte"][0];
  }
  if (queryParams["filter.p.m.custom.restock"]) {
    queryParams["filter.p.m.custom.restock"].forEach(function (value) {
      const restockValue = value === '0' ? parseInt(value) + 2 : parseInt(value);
      searchStr += "&restock=" + String(restockValue);
    });
  }
  if (queryParams["page"]) {
    searchStr += "&page=" + queryParams["page"][0];
  }
  if (queryParams["sort_by"]) {
    if (queryParams["sort_by"][0] === "price-ascending") {
      searchStr += "&order=1";
    } else if (queryParams["sort_by"][0] === "price-descending") {
      searchStr += "&order=2";
    }
  }

  // ページャー、history.pushState用のクエリパラメータを取得
  const currentPage = parseInt(queryParams.page) || 1;
  const searchParams = new URLSearchParams(window.location.search);

  fetch(`https://${host}/front-api/search/product?` + searchStr)
    .then(response => response.json())
    .then(data => {
      // console.log('Fetch success:', data);
      const rootElement = document.getElementById('root');
      if (data.status === "ok" && rootElement) {
        if (data.data.count !== 0) {
          ReactDOM.render(
            <>
              <ProductList products={data.data.products} />
              {data.data.count > 48 && (
                <Pagination
                  totalPages={Math.ceil(data.data.count / 48)}
                  currentPage={currentPage}
                  queryParams={searchParams}
                />
              )}
            </>,
            rootElement
          );
          updatePriceRange(data.data);
        } else {
          ReactDOM.render(
            <ErrorMessage message="検索結果が見つかりませんでした。" />,
            rootElement
          );
        }
        updateSearchResultCount(data.data);
        // URLを更新
        const newUrl = `/search?${searchParams.toString()}`;
        history.pushState({ path: newUrl }, '', newUrl);
      } else {
        updateSearchResultCount({ count: 0 });
      }
    }).catch(error => {
      console.error('Fetch error:', error);
      const rootElement = document.getElementById('root');
      if (rootElement) {
        ReactDOM.render(
          <ErrorMessage message="データの取得に失敗しました。" />,
          rootElement
        );
      }
      updateSearchResultCount({ count: 0 });
    });
}

// DOMContentLoadedイベントのリスナーを追加
document.addEventListener('DOMContentLoaded', function () {
  if (window.location.pathname === '/search') {
    fetchDataAndRender();

    // theme.jsに追加したカスタムイベントでReactコンポーネントを再レンダリング
    document.addEventListener('facet:updated', function () {
      fetchDataAndRender();
    });
  }
});
